// 事故報告責任者（事業者向け）のモデル。
// 事業者が行える操作は一覧・詳細・登録・削除に限定される。登録内容の変更・再有効化と、
// 社内メモ・変更履歴・同一人物関係はGO社内向け画面（halifax）の機能であり、
// 本画面のAPIレスポンスにも含まれない。

// eラーニング受講状況
export const LEARNING_STATUS = {
  /** 期限超過（未合格で受講期限を過ぎている） */
  OVERDUE: "OVERDUE",
  /** 未受講（未合格で受講期限内） */
  NOT_TAKEN: "NOT_TAKEN",
  /** 合格 */
  PASSED: "PASSED",
  /** 対象外（未合格で研修プログラムが公開終了している） */
  NOT_APPLICABLE: "NOT_APPLICABLE",
} as const;

export type LearningStatus =
  (typeof LEARNING_STATUS)[keyof typeof LEARNING_STATUS];

/** 事故報告責任者（一覧行）。1レコード＝1営業所 */
export type AccidentReportPerson = {
  id: number;
  officeId: number;
  officeName: string | null;
  /** 氏名。暗号化カラム追加前に登録された既存レコードでは null */
  name: string | null;
  email: string | null;
  /** 電話番号。暗号化カラム追加前に登録された既存レコードでは null */
  phoneNumber: string | null;
  /** true=登録中 / false=削除済み */
  isActive: boolean;
  /** 受講状況の代表値。受講割当がない場合は null */
  learningStatus: LearningStatus | null;
};

/** 研修プログラム単位のeラーニング受講状況 */
export type AccidentReportPersonLearningStatusItem = {
  learningProgramId: number;
  learningProgramTitle: string | null;
  /** 研修プログラムが公開中か（公開終了は「合格」か「対象外」のみになる） */
  published: boolean;
  status: LearningStatus;
  deadline: string | null;
  passedAt: string | null;
};

/** 事故報告責任者 詳細 */
export type AccidentReportPersonDetail = AccidentReportPerson & {
  learningStatuses: AccidentReportPersonLearningStatusItem[];
};

/** 事故報告責任者 登録リクエスト */
export type CreateAccidentReportPersonRequest = {
  officeId: number;
  name: string;
  email: string;
  phoneNumber: string;
  /** 国交省フロー組込み責任者としても登録するか（入力値を複製して別レコードを作る） */
  registerAsMlitFlowPerson: boolean;
};

/** 登録上限の判定結果 */
export type RegistrationLimit = {
  officeId: number;
  /** 登録中の責任者数（削除済みはカウントしない） */
  activePersonCount: number;
  registrationLimit: number;
  limitReached: boolean;
};

/** 責任者登録数の案内表示 */
export type RegistrationGuide = {
  shouldDisplay: boolean;
  offices: {
    officeId: number;
    officeName: string | null;
    activePersonCount: number;
  }[];
};

/** 国交省フロー組込み責任者 */
export type MlitFlowPerson = {
  id: number;
  officeId: number;
  officeName: string | null;
  // 個人情報カラムの復号に失敗した場合、サーバは null を返す
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
};

/** 国交省フロー組込み責任者 登録リクエスト */
export type MlitFlowPersonRequest = {
  officeId: number;
  name: string;
  email: string;
  phoneNumber: string;
};

/** 氏名の上限文字数 */
export const NAME_MAX_LENGTH = 100;
/** メールアドレスの上限文字数 */
export const EMAIL_MAX_LENGTH = 100;
/** 電話番号の上限文字数 */
export const PHONE_NUMBER_MAX_LENGTH = 20;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// 数字とハイフン。先頭・末尾のハイフンおよび連続ハイフンは不可（サーバ側のバリデーションと同一）
const PHONE_NUMBER_PATTERN = /^[0-9]+(-[0-9]+)*$/;
// 入力時に許可しない文字種（英字・括弧等）をその場で弾くための文字集合。
// 先頭・末尾・連続ハイフンの禁止は文字種の制約ではないため、ここでは検証せず送信前の
// validateAccidentReportPersonForm に委ねる
const PHONE_NUMBER_ALLOWED_CHARS = /[^0-9-]/g;

/**
 * 電話番号欄の入力値から、許可されない文字（数字・ハイフン以外）を除去する.<br>
 * キー入力・貼り付け・IME確定のいずれでも、許可されない文字が入力欄へ反映されないようにするために使う
 * （事故報告責任者フォーム・国交省フロー組込み責任者フォームで共通利用する）。
 *
 * 全角数字（０-９）も許可されない文字として黙って削除される。全角→半角変換を挟んでから
 * 許可文字チェックをすればより親切だが、送信前に入力欄で値を確認できるため実害は小さく、
 * この一箇所のためだけに全角/半角正規化という新しい設計判断を持ち込むほどではないと判断し
 * 見送っている。将来的に電話番号入力全般でこの種の変換が必要になった場合は、共通ユーティリティ
 * として切り出すことを検討する。
 *
 * @param value 入力値
 *
 * @return 数字とハイフンのみを残した文字列
 */
export const sanitizePhoneNumberInput = (value: string): string =>
  value.replace(PHONE_NUMBER_ALLOWED_CHARS, "");

export type AccidentReportPersonFormInput = {
  name: string;
  email: string;
  phoneNumber: string;
  officeId: number | null;
};

export type AccidentReportPersonFormError = {
  name?: "required" | "format";
  email?: "required" | "format";
  phoneNumber?: "required" | "format";
  officeId?: boolean;
};

/**
 * 責任者フォームの入力値を検証する。
 * 必須・形式・文字数上限はサーバ側（Jakarta Bean Validation）と同一条件にそろえる。
 * 氏名の重複チェックと登録上限はサーバ側でのみ判定する。
 */
export const validateAccidentReportPersonForm = (
  input: AccidentReportPersonFormInput,
): AccidentReportPersonFormError => {
  const name = input.name.trim();
  const email = input.email.trim();
  const phoneNumber = input.phoneNumber.trim();

  return {
    name: !name
      ? "required"
      : name.length > NAME_MAX_LENGTH
        ? "format"
        : undefined,
    email: !email
      ? "required"
      : !EMAIL_PATTERN.test(email) || email.length > EMAIL_MAX_LENGTH
        ? "format"
        : undefined,
    phoneNumber: !phoneNumber
      ? "required"
      : !PHONE_NUMBER_PATTERN.test(phoneNumber) ||
          phoneNumber.length > PHONE_NUMBER_MAX_LENGTH
        ? "format"
        : undefined,
    officeId: input.officeId === null,
  };
};

export const hasFormError = (errors: AccidentReportPersonFormError): boolean =>
  Object.values(errors).some((it) => it);
